import { Link } from "react-router-dom";
import { SubPageShell, PageFaq } from "@/components/site/SubPage";
import CasoStudio from "@/components/site/CasoStudio";
import { useSeo } from "@/lib/useSeo";
import { openCallReason } from "@/lib/site";

const FAQ = [
  {
    q: "Cosa fa esattamente un revenue manager?",
    a: "Decide a che prezzo vendi ogni notte, su ogni portale, e lo aggiusta in continuazione: domanda, eventi, stagione, concorrenza. Non è un listino scritto una volta l'anno: è un lavoro quotidiano sui tuoi prezzi e sul tuo calendario.",
  },
  {
    q: "Devo cambiare property manager per lavorare con te?",
    a: "Dipende da cosa ti serve. Se il tuo property manager fa bene check-in e pulizie ma lascia i prezzi al caso, posso occuparmi io solo di prezzi, calendario e annunci. Se invece vuoi un interlocutore unico, valutiamo insieme la strada migliore per la tua struttura.",
  },
  {
    q: "Gli account e le recensioni restano miei?",
    a: "Sì, sempre. Booking e Airbnb sono intestati a te: profili, storico e recensioni sono patrimonio tuo. Io ci lavoro dentro ogni giorno e tu controlli ogni mossa dall'app che tiene allineati i calendari — inclusa, la pago io.",
  },
  {
    q: "Quanto costa?",
    a: "La gestione continua parte da 250 €/mese, a costo fisso e senza percentuali sui tuoi incassi. Nessun vincolo: disdici quando vuoi con 30 giorni di preavviso.",
  },
];

export default function RevenueManagerPage() {
  useSeo({
    title: "Revenue manager per case vacanze in Sicilia | YU YieldUp",
    description:
      "Revenue management per case vacanze, B&B e strutture ricettive in Sicilia: prezzi aggiornati ogni giorno, calendari allineati, annunci curati e report chiari. Una persona vera, a costo fisso.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ProfessionalService",
          name: "Revenue management per case vacanze e strutture ricettive",
          provider: { "@type": "Person", name: "Luciano Di Blasi", jobTitle: "Founder e Revenue Manager", worksFor: { "@type": "Organization", name: "YU YieldUp" } },
          areaServed: { "@type": "AdministrativeArea", name: "Sicilia" },
          description:
            "Gestione quotidiana di prezzi, calendari e annunci per case vacanze, B&B e strutture ricettive: strategia tariffaria, coerenza dei prezzi tra sito e portali, occupazione, ADR e report mensili in parole semplici.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ],
    },
  });

  return (
    <SubPageShell callSource="revenue-manager">
      <section data-testid="revenue-manager-page" className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <p className="section-kicker">Il mio lavoro</p>
        <h1 className="mt-4 font-heading text-3xl font-black uppercase leading-[1.02] tracking-tighter text-[#FAF7F2] sm:text-5xl">
          Revenue manager per case vacanze e strutture ricettive in Sicilia
        </h1>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
          I grandi hotel hanno una persona che ogni giorno decide i prezzi, guarda la domanda e muove il calendario. La tua struttura no — e infatti spesso vende al prezzo sbagliato. Quel lavoro, sulla tua struttura, lo faccio io.
        </p>

        <div className="mt-10 overflow-hidden border border-[#383029]">
          <img
            src="/images/luciano.jpg"
            alt="Luciano, founder e revenue manager di YU YieldUp"
            className="aspect-[4/3] w-full object-cover object-top grayscale contrast-110 sm:aspect-[21/9]"
            loading="lazy"
          />
        </div>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">La mia storia, in breve</h2>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0]">
          Faccio l'host da dieci anni. Con la mia società, <span className="font-medium text-[#FAF7F2]">OpenSicily Homes</span>, gestisco diversi appartamenti in Sicilia: il tuo lavoro è anche il mio, tutti i giorni.
        </p>
        <p className="mt-4 text-base font-light leading-relaxed text-[#C4BBB0]">
          In questi anni mi sono scontrato con problemi a cui nessuno sapeva rispondere: prezzi lasciati al caso, agenzie che sparivano dopo la firma, strumenti seri riservati solo ai grandi hotel. Ogni volta dovevo inventarmi la soluzione da solo. YU YieldUp nasce da lì: gli stessi strumenti e lo stesso metodo dei grandi hotel, adattati alla tua struttura — a costo fisso, senza percentuali sui tuoi incassi.
        </p>
        <p className="mt-4 border-l-2 border-[#E05A2C] pl-6 text-base font-light leading-relaxed text-[#C4BBB0]">
          Chi entra in YieldUp ha me come punto di riferimento diretto: niente ticket, niente attese, quando scrivi rispondo io. Per questo seguo personalmente un numero massimo di strutture alla volta — quando sono piene, le nuove richieste aspettano.
        </p>

        <div className="mt-14">
          <CasoStudio />
        </div>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Cosa faccio per la tua struttura, in pratica</h2>
        <ul className="mt-6 space-y-4">
          {[
            "Prezzi aggiornati ogni giorno: domanda, eventi, stagione e concorrenza, non un listino scritto una volta l'anno",
            "Calendario e disponibilità sempre allineati su Booking, Airbnb e gli altri portali",
            "Annunci curati: testi, foto in evidenza e regole pensate per vendere, non solo per esserci",
            "Coerenza dei prezzi tra il tuo sito e i portali: niente prezzi diversi che confondono l'ospite",
            "Occupazione e prezzo medio sotto controllo: riempire svendendo non è un risultato",
            "Offerte e promozioni nei periodi deboli, decise sui numeri e non a istinto",
            "Controllo del prezzo netto che paga davvero l'ospite, tra commissioni e supplementi",
            "A fine mese un report in parole semplici: com'è andata, perché, e cosa faremo il mese dopo",
          ].map((item) => (
            <li key={item} className="flex gap-4 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
              <span className="shrink-0 font-heading font-black text-[#E05A2C]">—</span>
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Come lavoriamo insieme</h2>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0]">
          Gli account restano intestati a te e le recensioni sono patrimonio tuo. Io lavoro dentro la tua struttura ogni giorno e tu vedi tutto da un'unica app — prezzi, calendari e prenotazioni — che è inclusa e la pago io. E se un giorno decidi che le nostre strade si dividono, porti via tutto.
        </p>

        <div className="mt-10 border border-[#4D2C20] bg-[#1A1714] p-6 sm:p-8">
          <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Vuoi sapere cosa farei con la tua struttura?</p>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
            Prenota una chiamata gratuita di 30 minuti: mi racconti la tua situazione e ti dico subito, in chiaro, cosa migliorerei e se posso esserti utile.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              data-testid="revenue-manager-cta-call"
              onClick={() => openCallReason("revenue-manager")}
              className="btn-glow rounded-full bg-[#E05A2C] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
            >
              Prenota 30 minuti gratis
            </button>
            <Link to="/" data-testid="revenue-manager-back-home" className="text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]">
              Torna alla home
            </Link>
          </div>
        </div>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Domande frequenti</h2>
        <PageFaq items={FAQ} />
      </section>
    </SubPageShell>
  );
}
